import { useState, useEffect } from 'react';
import Button from '@components/ui/Button';
import Toggle from '@components/ui/Toggle';
import { useApiClient } from '@hooks/useApiClient';
import { getApiErrorMessage } from '@utils/api/ApiClient';
import { toast } from '@utils/toast';
import {
    getSaleCompletionWhatsAppConfig,
    updateSaleCompletionWhatsAppConfig,
    getMultiStoreAgentConfig,
    updateMultiStoreAgentConfig,
    getBrandAgentConfig,
    getStoreOperationsConfig,
    updateStoreOperationsConfig,
} from '../../../../services/storeManagementService';
import type {
    StoreSearchResult,
    SaleCompletionWhatsAppConfig,
    MultiStoreAgentConfigPayload,
    BrandAgentConfig,
    StoreOperationsConfig,
} from '../../../../types/api.types';
import {
    SettingsSection,
    SettingsRow,
    SettingsSkeleton,
    SettingsBanner,
    StaffMultiSelect,
} from '../shared';

interface SettingsTabProps {
    store: StoreSearchResult;
    storeBrandId: string;
    onOpenBrandSettings: () => void;
}

const DEFAULT_RECEIPT_CONFIG: SaleCompletionWhatsAppConfig = {
    enabled: false,
    whatsappBusinessNumber: null,
};

const DEFAULT_AGENT_CONFIG: MultiStoreAgentConfigPayload = {
    enabled: false,
    defaultStore: false,
    sendOrderRequestOnWhatsApp: false,
    orderNotificationReceivers: [],
};

const DEFAULT_BRAND_AGENT_CONFIG: BrandAgentConfig = {
    checkDeliveryRequest: false,
    checkBulkOrderRequest: false,
};

const DEFAULT_STORE_OPS_CONFIG: StoreOperationsConfig = {
    phone: '',
    whatsappNumber: '',
    city: '',
    landmark: '',
    nearbyAreas: [],
    timings: [],
};

const DAYS_OF_WEEK: Record<number, string> = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday',
};

function isoToTimeInput(iso: string): string {
    try {
        return new Date(iso).toISOString().slice(11, 16);
    } catch {
        return '09:00';
    }
}

function timeInputToIso(timeValue: string): string {
    return new Date(`1970-01-01T${timeValue}:00.000Z`).toISOString();
}

function normalizeBusinessNumberInput(value: string): string {
    return value.replace(/\D/g, '').slice(0, 10);
}

export default function SettingsTab({
    store,
    storeBrandId,
    onOpenBrandSettings,
}: SettingsTabProps) {
    const [fetchLoading, setFetchLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const [receiptConfig, setReceiptConfig] =
        useState<SaleCompletionWhatsAppConfig>(DEFAULT_RECEIPT_CONFIG);
    const [agentConfig, setAgentConfig] =
        useState<MultiStoreAgentConfigPayload>(DEFAULT_AGENT_CONFIG);
    const [brandAgentConfig, setBrandAgentConfig] = useState<BrandAgentConfig>(
        DEFAULT_BRAND_AGENT_CONFIG
    );
    const [storeOpsConfig, setStoreOpsConfig] =
        useState<StoreOperationsConfig>(DEFAULT_STORE_OPS_CONFIG);

    const { loading: receiptSaveLoading, callApi: callReceiptApi } = useApiClient({ toast: true });
    const { loading: agentSaveLoading, callApi: callAgentApi } = useApiClient({ toast: true });
    const { loading: storeOpsSaveLoading, callApi: callStoreOpsApi } = useApiClient({
        toast: true,
    });

    useEffect(() => {
        const fetchData = async () => {
            setFetchLoading(true);
            setLoadFailed(false);

            try {
                const [receiptRes, agentRes, brandConfigRes, storeOpsRes] = await Promise.all([
                    getSaleCompletionWhatsAppConfig(store._id, storeBrandId),
                    getMultiStoreAgentConfig(store._id, storeBrandId),
                    getBrandAgentConfig(storeBrandId),
                    getStoreOperationsConfig(store._id, storeBrandId),
                ]);

                setReceiptConfig({
                    ...DEFAULT_RECEIPT_CONFIG,
                    ...receiptRes.data.saleCompletionWhatsApp,
                });

                const receivers =
                    agentRes.data.multiStoreAgentConfig.orderNotificationReceivers ?? [];
                setAgentConfig({
                    enabled: agentRes.data.multiStoreAgentConfig.enabled ?? false,
                    defaultStore: agentRes.data.multiStoreAgentConfig.defaultStore ?? false,
                    sendOrderRequestOnWhatsApp:
                        agentRes.data.multiStoreAgentConfig.sendOrderRequestOnWhatsApp ?? false,
                    orderNotificationReceivers: receivers.map((r) => r.storeMemberId),
                });

                setBrandAgentConfig({ ...DEFAULT_BRAND_AGENT_CONFIG, ...brandConfigRes.data });

                const ops = storeOpsRes.data?.operationsConfig;
                setStoreOpsConfig({
                    phone: ops?.phone ?? '',
                    whatsappNumber: ops?.whatsappNumber ?? '',
                    city: ops?.city ?? '',
                    landmark: ops?.landmark ?? '',
                    nearbyAreas: ops?.nearbyAreas ?? [],
                    timings: ops?.timings ?? [],
                });
            } catch (err: unknown) {
                setLoadFailed(true);
                toast.error(getApiErrorMessage(err));
            } finally {
                setFetchLoading(false);
            }
        };

        fetchData();
    }, [store._id, storeBrandId]);

    const showBrandRoutingBanner =
        brandAgentConfig.checkDeliveryRequest || brandAgentConfig.checkBulkOrderRequest;

    const receiverCount = agentConfig.orderNotificationReceivers.length;

    const handleSaveReceipt = async () => {
        const whatsappBusinessNumber = receiptConfig.whatsappBusinessNumber?.trim() || null;

        if (receiptConfig.enabled && whatsappBusinessNumber?.length !== 10) {
            toast.error(
                'Enter a valid 10-digit WhatsApp business number when receipts are enabled.'
            );
            return;
        }

        await callReceiptApi(
            () =>
                updateSaleCompletionWhatsAppConfig(store._id, storeBrandId, {
                    enabled: receiptConfig.enabled,
                    whatsappBusinessNumber,
                }),
            (response) => {
                setReceiptConfig(response.data.saleCompletionWhatsApp);
                toast.success('Receipt settings saved.');
            }
        );
    };

    const handleSaveAgent = async () => {
        await callAgentApi(
            () => updateMultiStoreAgentConfig(store._id, storeBrandId, agentConfig),
            (response) => {
                const receivers =
                    response.data.multiStoreAgentConfig.orderNotificationReceivers ?? [];
                setAgentConfig({
                    enabled: response.data.multiStoreAgentConfig.enabled,
                    defaultStore: response.data.multiStoreAgentConfig.defaultStore,
                    sendOrderRequestOnWhatsApp:
                        response.data.multiStoreAgentConfig.sendOrderRequestOnWhatsApp ?? false,
                    orderNotificationReceivers: receivers.map((r) => r.storeMemberId),
                });
                toast.success('Chatbot settings saved.');
            }
        );
    };

    const handleSaveStoreOps = async () => {
        await callStoreOpsApi(
            () =>
                updateStoreOperationsConfig(store._id, storeBrandId, {
                    ...storeOpsConfig,
                    phone: storeOpsConfig.phone?.trim() || null,
                    whatsappNumber: storeOpsConfig.whatsappNumber?.trim() || null,
                    city: storeOpsConfig.city?.trim() || null,
                    landmark: storeOpsConfig.landmark?.trim() || null,
                }),
            (response) => {
                const ops = response.data?.operationsConfig;
                setStoreOpsConfig({
                    phone: ops?.phone ?? '',
                    whatsappNumber: ops?.whatsappNumber ?? '',
                    city: ops?.city ?? '',
                    landmark: ops?.landmark ?? '',
                    nearbyAreas: ops?.nearbyAreas ?? [],
                    timings: ops?.timings ?? [],
                });
                toast.success('Operations settings saved.');
            }
        );
    };

    if (fetchLoading) {
        return <SettingsSkeleton />;
    }

    if (loadFailed) {
        return <p className="text-sm text-[#64748b] text-center py-10">Could not load settings.</p>;
    }

    return (
        <div className="space-y-6">
            <p className="text-sm text-[#64748b]">
                Receipts: {receiptConfig.enabled ? 'on' : 'off'}
                {' · '}
                Chatbot: {agentConfig.enabled ? 'on' : 'off'}
                {agentConfig.defaultStore ? ' · Default store for brand' : ''}
                {' · '}
                {receiverCount} staff member{receiverCount === 1 ? '' : 's'} to notify
            </p>

            {showBrandRoutingBanner && (
                <SettingsBanner
                    action={
                        <button
                            type="button"
                            onClick={onOpenBrandSettings}
                            className="text-xs font-medium text-[#2563eb] hover:text-[#1d4ed8] underline underline-offset-2"
                        >
                            Open brand settings
                        </button>
                    }
                >
                    Delivery and bulk order handling is managed at the brand level. The settings
                    below only affect this store.
                </SettingsBanner>
            )}

            <SettingsSection
                title="Sale WhatsApp Receipt"
                description="Automatically send customers a WhatsApp order summary after a successful bill."
            >
                <SettingsRow
                    label="Send WhatsApp receipt after sale"
                    control={
                        <Toggle
                            label="Send WhatsApp receipt after sale"
                            value={receiptConfig.enabled}
                            onChange={(v) => {
                                setReceiptConfig((prev) => ({ ...prev, enabled: v }));
                            }}
                        />
                    }
                />

                <div className="px-4 py-3.5 border-t border-[#f1f5f9]">
                    <label
                        htmlFor="whatsapp-business-number"
                        className="block text-sm font-medium text-[#1e293b]"
                    >
                        Send from WhatsApp Business Number
                    </label>
                    <p className="text-xs text-[#64748b] mt-0.5">
                        {receiptConfig.enabled
                            ? 'Required when enabled.'
                            : 'Enter the registered business number key used to send receipts.'}
                    </p>
                    <input
                        id="whatsapp-business-number"
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={receiptConfig.whatsappBusinessNumber ?? ''}
                        disabled={!receiptConfig.enabled}
                        placeholder="10-digit mobile number"
                        onChange={(e) => {
                            const value = normalizeBusinessNumberInput(e.target.value);
                            setReceiptConfig((prev) => ({
                                ...prev,
                                whatsappBusinessNumber: value || null,
                            }));
                        }}
                        className="mt-2 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed"
                    />
                </div>

                <div className="px-4 pb-4">
                    <Button
                        variant="primary"
                        isLoading={receiptSaveLoading}
                        onClick={handleSaveReceipt}
                        className="w-full"
                    >
                        Save receipt settings
                    </Button>
                </div>
            </SettingsSection>

            <SettingsSection
                title="WhatsApp chatbot"
                description="How this store handles customer messages through the chatbot."
            >
                <SettingsRow
                    label="Chatbot enabled for this store"
                    description="Allow customers to reach this store through the brand WhatsApp chatbot."
                    control={
                        <Toggle
                            label="Chatbot enabled for this store"
                            value={agentConfig.enabled}
                            onChange={(v) => {
                                setAgentConfig((prev) => ({ ...prev, enabled: v }));
                            }}
                        />
                    }
                />
                <SettingsRow
                    label="Default store for the brand"
                    description="When the chatbot can't tell which store a customer means, route them here. Only one store per brand can be the default."
                    control={
                        <Toggle
                            label="Default store for the brand"
                            value={agentConfig.defaultStore}
                            onChange={(v) => {
                                setAgentConfig((prev) => ({ ...prev, defaultStore: v }));
                            }}
                        />
                    }
                />

                <SettingsRow
                    label="Send order request on WhatsApp"
                    description="After a customer sends a store request, WhatsApp the staff selected below from the BottleShop Alerts number."
                    control={
                        <Toggle
                            label="Send order request on WhatsApp"
                            value={agentConfig.sendOrderRequestOnWhatsApp}
                            onChange={(v) => {
                                setAgentConfig((prev) => ({
                                    ...prev,
                                    sendOrderRequestOnWhatsApp: v,
                                }));
                            }}
                        />
                    }
                />

                <div className="px-4 py-3.5">
                    <p className="text-sm font-medium text-[#1e293b]">Staff to notify</p>
                    <p className="text-xs text-[#64748b] mt-0.5">
                        Choose who receives WhatsApp alerts when the chatbot gets an order.
                    </p>

                    <StaffMultiSelect
                        storeBrandId={storeBrandId}
                        storeId={store._id}
                        selectedIds={agentConfig.orderNotificationReceivers}
                        onChange={(ids) => {
                            setAgentConfig((prev) => ({
                                ...prev,
                                orderNotificationReceivers: ids,
                            }));
                        }}
                    />
                </div>

                <div className="px-4 pb-4">
                    <Button
                        variant="primary"
                        isLoading={agentSaveLoading}
                        onClick={handleSaveAgent}
                        className="w-full"
                    >
                        Save chatbot settings
                    </Button>
                </div>
            </SettingsSection>

            <SettingsSection
                title="Store Operations"
                description="Contact details, location, and opening hours shown on the public microsite."
            >
                <div className="px-4 py-3.5 space-y-4">
                    {/* Contact */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label
                                htmlFor="store-ops-phone"
                                className="block text-sm font-medium text-[#1e293b]"
                            >
                                Phone number
                            </label>
                            <input
                                id="store-ops-phone"
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={storeOpsConfig.phone ?? ''}
                                placeholder="10-digit number"
                                onChange={(e) => {
                                    const value = normalizeBusinessNumberInput(e.target.value);
                                    setStoreOpsConfig((prev) => ({ ...prev, phone: value }));
                                }}
                                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="store-ops-whatsapp"
                                className="block text-sm font-medium text-[#1e293b]"
                            >
                                WhatsApp number
                            </label>
                            <input
                                id="store-ops-whatsapp"
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={storeOpsConfig.whatsappNumber ?? ''}
                                placeholder="10-digit number"
                                onChange={(e) => {
                                    const value = normalizeBusinessNumberInput(e.target.value);
                                    setStoreOpsConfig((prev) => ({
                                        ...prev,
                                        whatsappNumber: value,
                                    }));
                                }}
                                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Location */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label
                                htmlFor="store-ops-city"
                                className="block text-sm font-medium text-[#1e293b]"
                            >
                                City
                            </label>
                            <input
                                id="store-ops-city"
                                type="text"
                                value={storeOpsConfig.city ?? ''}
                                placeholder="e.g. Mumbai"
                                onChange={(e) =>
                                    setStoreOpsConfig((prev) => ({
                                        ...prev,
                                        city: e.target.value,
                                    }))
                                }
                                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="store-ops-landmark"
                                className="block text-sm font-medium text-[#1e293b]"
                            >
                                Landmark
                            </label>
                            <input
                                id="store-ops-landmark"
                                type="text"
                                value={storeOpsConfig.landmark ?? ''}
                                placeholder="e.g. Near Central Mall"
                                onChange={(e) =>
                                    setStoreOpsConfig((prev) => ({
                                        ...prev,
                                        landmark: e.target.value,
                                    }))
                                }
                                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div>
                        <label
                            htmlFor="store-ops-nearby"
                            className="block text-sm font-medium text-[#1e293b]"
                        >
                            Nearby areas
                        </label>
                        <p className="text-xs text-[#64748b] mt-0.5">
                            Comma-separated list of nearby areas or neighbourhoods.
                        </p>
                        <input
                            id="store-ops-nearby"
                            type="text"
                            value={(storeOpsConfig.nearbyAreas ?? []).join(', ')}
                            placeholder="e.g. Bandra, Juhu, Andheri"
                            onChange={(e) => {
                                const areas = e.target.value
                                    .split(',')
                                    .map((s) => s.trimStart())
                                    .filter(Boolean);
                                setStoreOpsConfig((prev) => ({ ...prev, nearbyAreas: areas }));
                            }}
                            className="mt-1.5 w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                        />
                    </div>

                    {/* Opening hours */}
                    <div>
                        <p className="text-sm font-medium text-[#1e293b] mb-2">Opening hours</p>
                        <div className="rounded-xl border border-[#e2e8f0] divide-y divide-[#f1f5f9]">
                            {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                                const timing = (storeOpsConfig.timings ?? []).find(
                                    (t) => t.day === day
                                );
                                const isOpen = Boolean(timing);
                                const openTime = timing
                                    ? isoToTimeInput(timing.openingTime)
                                    : '09:00';
                                const closeTime = timing
                                    ? isoToTimeInput(timing.closingTime)
                                    : '21:00';

                                return (
                                    <div key={day} className="flex items-center gap-3 px-3 py-2.5">
                                        <input
                                            type="checkbox"
                                            id={`day-${day}`}
                                            checked={isOpen}
                                            onChange={(e) => {
                                                setStoreOpsConfig((prev) => {
                                                    const current = prev.timings ?? [];
                                                    if (e.target.checked) {
                                                        return {
                                                            ...prev,
                                                            timings: [
                                                                ...current,
                                                                {
                                                                    day,
                                                                    openingTime:
                                                                        timeInputToIso('09:00'),
                                                                    closingTime:
                                                                        timeInputToIso('21:00'),
                                                                },
                                                            ].sort((a, b) => a.day - b.day),
                                                        };
                                                    }
                                                    return {
                                                        ...prev,
                                                        timings: current.filter(
                                                            (t) => t.day !== day
                                                        ),
                                                    };
                                                });
                                            }}
                                            className="w-4 h-4 rounded accent-[#6e64f0] cursor-pointer"
                                        />
                                        <label
                                            htmlFor={`day-${day}`}
                                            className="w-24 text-sm font-medium text-[#1e293b] cursor-pointer select-none"
                                        >
                                            {DAYS_OF_WEEK[day]}
                                        </label>

                                        {isOpen ? (
                                            <div className="flex items-center gap-2 flex-1">
                                                <input
                                                    type="time"
                                                    value={openTime}
                                                    onChange={(e) => {
                                                        setStoreOpsConfig((prev) => ({
                                                            ...prev,
                                                            timings: (prev.timings ?? []).map(
                                                                (t) =>
                                                                    t.day === day
                                                                        ? {
                                                                              ...t,
                                                                              openingTime:
                                                                                  timeInputToIso(
                                                                                      e.target.value
                                                                                  ),
                                                                          }
                                                                        : t
                                                            ),
                                                        }));
                                                    }}
                                                    className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                                                />
                                                <span className="text-xs text-[#94a3b8]">to</span>
                                                <input
                                                    type="time"
                                                    value={closeTime}
                                                    onChange={(e) => {
                                                        setStoreOpsConfig((prev) => ({
                                                            ...prev,
                                                            timings: (prev.timings ?? []).map(
                                                                (t) =>
                                                                    t.day === day
                                                                        ? {
                                                                              ...t,
                                                                              closingTime:
                                                                                  timeInputToIso(
                                                                                      e.target.value
                                                                                  ),
                                                                          }
                                                                        : t
                                                            ),
                                                        }));
                                                    }}
                                                    className="h-9 px-2 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-transparent"
                                                />
                                            </div>
                                        ) : (
                                            <span className="text-sm text-[#94a3b8]">Closed</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="px-4 pb-4">
                    <Button
                        variant="primary"
                        isLoading={storeOpsSaveLoading}
                        onClick={handleSaveStoreOps}
                        className="w-full"
                    >
                        Save operations settings
                    </Button>
                </div>
            </SettingsSection>
        </div>
    );
}
